import { React, classNames, css, hooks } from 'jimu-core';
import { styled } from 'jimu-theme';
const ThumbSize = 14;
const SliderSize = 2;
const thumbRootStyle = css `
  appearance: none;
  pointer-events: all;
  background-color: var(--ref-palette-white);
  border: 2px solid var(--sys-color-action-selected);
  border-radius: 50%;
  cursor: pointer;
  height: ${ThumbSize}px;
  width:${ThumbSize}px;
  position: relative;
  box-sizing: border-box;
`;
const Root = styled('div', {
    name: 'MultiRangeSlider',
    part: 'root'
})(({ styleState }) => {
    const { baseZindex } = styleState;
    return css `
    width: 100%;
    position: relative;
    .range-input {
      width: 100%;
      position: absolute;
      top: 50%;
      position: absolute;
      pointer-events: none;
      height: ${SliderSize}px;
      background-color: transparent;
      appearance: none;
      box-sizing: border-box;
      -webkit-tap-highlight-color: transparent;
      &::-webkit-slider-thumb {
        ${thumbRootStyle}
      }
      &::-moz-range-thumb {
        ${thumbRootStyle}
      }
      &:hover {
        &::-webkit-slider-thumb {
          border-color: var(--sys-color-action-selected-hover);
        }
        &::-moz-range-thumb {
          border-color: var(--sys-color-action-selected-hover);
        }
      }
      &:disabled {
        &::-webkit-slider-thumb {
          background-color: var(--sys-color-action-disabled);
          border-color: var(--sys-color-divider-secondary);
          pointer-events: none;
        }
        &::-moz-range-thumb {
          background-color: var(--sys-color-action-disabled);
          border-color: var(--sys-color-divider-secondary);
          pointer-events: none;
        }
      }
      &:focus {
        outline: none;
        background: transparent;
        border: transparent;
        &::-webkit-slider-runnable-track {
          background: transparent;
          border: transparent;
        }
        &::-moz-range-track {
          background: transparent;
          border: transparent;
        }
        &::-webkit-slider-thumb {
          outline: 2px solid var(--sys-color-action-focus);
          outline-offset: 2px;
        }
        &::-moz-range-thumb {
          outline: 2px solid var(--sys-color-action-focus);
          outline-offset: 2px;
        }
      }
    }

    .range-input:first-of-type:hover ~ .slider > .multi-range-slider--value.left-value.label-visible {
      opacity: 1;
    }
    .range-input:last-of-type:hover ~ .slider > .multi-range-slider--value.right-value.label-visible {
      opacity: 1;
    }

    .jimu-keyboard-nav & {
      .range-input:first-of-type:focus {
        & ~ .slider > .multi-range-slider--value.left-value.label-visible {
          opacity: 1;
        }
      }
      .range-input:last-of-type:focus {
        & ~ .slider > .multi-range-slider--value.right-value.label-visible {
          opacity: 1;
        }
      }
    }

    .multi-range-slider--zindex-level-0 {
      z-index: ${baseZindex};
    }
    .multi-range-slider--zindex-level-1 {
      z-index: ${baseZindex + 1};
    }
    .multi-range-slider--zindex-level-2 {
      z-index: ${baseZindex + 2};
    }
    .multi-range-slider--zindex-level-3 {
      z-index: ${baseZindex + 3};
    }
    .multi-range-slider--zindex-level-4 {
      z-index: ${baseZindex + 4};
    }
    .multi-range-slider--zindex-level-5 {
      z-index: ${baseZindex + 5};
    }
    .multi-range-slider--value {
      opacity: 0;
    }
  `;
});
const SliderRoot = styled('div', {
    name: 'MultiRangeSlider',
    part: 'slider'
})(() => {
    return css `
    position: relative;
    left: ${ThumbSize / 2}px;
    right: ${ThumbSize / 2}px;
    height: ${ThumbSize}px;
    width: calc(100% - ${ThumbSize}px);
    display: flex;
    align-items: center;
    .multi-range-slider--track,
    .multi-range-slider--range,
    .multi-range-slider--left-value,
    .multi-range-slider--right-value {
      position: absolute;
    }
    .multi-range-slider--track,
    .multi-range-slider--range {
      border-radius: ${SliderSize}px;
      height: ${SliderSize}px;
      position: absolute;
    }
    .multi-range-slider--track {
      background-color: var(--sys-color-divider-secondary);
      width: 100%;
      left: 0;
    }
    .multi-range-slider--range {
      right: 0;
      background-color: var(--sys-color-action-selected);
    }
    .multi-range-slider--value {
      top: -26px;
      position: absolute;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--sys-spacing-1) var(--sys-spacing-2) 2px var(--sys-spacing-2);
      background-color: var(--ref-palette-neutral-400);
      font-family: var(--sys-typography-label2-font-family);
      font-weight: var(--sys-typography-label2-font-weight);
      font-size: var(--sys-typography-label2-font-size);
      line-height: 1;
      border-radius: var(--sys-shape-1);
      box-shadow: var(--sys-shadow-1);
      border-color: var(--sys-color-divider-primary);
      border-width: 1px;
      border-style: solid;
    }
    &.disabled {
      .multi-range-slider--track, .multi-range-slider--range {
        background-color: var(--sys-color-divider-secondary);
      }
    }
  `;
});
/**
 * The `MultiRangeSlider` component is provided to the user for selection of multiple ranges of numbers within a bounded range.
 *
 * ```ts
 * import { MultiRangeSlider } from 'jimu-ui'
 * ```
 */
export const MultiRangeSlider = React.forwardRef((props, ref) => {
    const { className, 'aria-label': ariaLabel, 'aria-valuetext-min': ariaValueTextMin, 'aria-valuetext-max': ariaValueTextMax, baseZindex = 0, min = 0, step = 1, max = 100, defaultMinValue, defaultMaxValue, minValue: propMinValue, maxValue: propMaxValue, tooltip = false, disabled = false, onChange, onAcceptValue: propOnAcceptValue } = props;
    const styleState = Object.assign(Object.assign({}, props), { baseZindex });
    const onAcceptValueRef = hooks.useLatest(propOnAcceptValue);
    const [minValue, setMinValue] = hooks.useControlled({
        controlled: propMinValue,
        default: propMinValue || defaultMinValue
    });
    const [maxValue, setMaxValue] = hooks.useControlled({
        controlled: propMaxValue,
        default: propMaxValue || defaultMaxValue
    });
    const minValueRef = hooks.useLatest(minValue);
    const maxValueRef = hooks.useLatest(maxValue);
    const minInputRef = React.useRef(null);
    const maxInputRef = React.useRef(null);
    const sliderRangeRef = React.useRef(null);
    const leftValueRef = React.useRef(null);
    const rightValueRef = React.useRef(null);
    const [leftThumbActivated, setLeftThumbActivated] = React.useState(false);
    // Convert to percentage
    const getPercent = React.useCallback((value) => Math.round(((value - min) / (max - min)) * 100), [min, max]);
    // Set width of the range to decrease from the left side
    React.useEffect(() => {
        if (maxInputRef.current) {
            const minPercent = getPercent(minValue);
            // Preceding with '+' converts the value from type string to type number
            const maxPercent = getPercent(+maxInputRef.current.value);
            if (sliderRangeRef.current) {
                sliderRangeRef.current.style.left = `${minPercent}%`;
                sliderRangeRef.current.style.width = `${maxPercent - minPercent}%`;
            }
            if (leftValueRef.current) {
                const width = leftValueRef.current.offsetWidth || 0;
                leftValueRef.current.style.left = `calc(${minPercent}% - ${width / 2}px)`;
            }
        }
    }, [minValue, getPercent]);
    // Set width of the range to decrease from the right side
    React.useEffect(() => {
        if (minInputRef.current) {
            const minPercent = getPercent(+minInputRef.current.value);
            const maxPercent = getPercent(maxValue);
            if (sliderRangeRef.current) {
                sliderRangeRef.current.style.width = `${maxPercent - minPercent}%`;
            }
            if (rightValueRef.current) {
                const width = rightValueRef.current.offsetWidth || 0;
                rightValueRef.current.style.left = `calc(${maxPercent}% - ${width / 2}px)`;
            }
        }
    }, [maxValue, getPercent]);
    const handleMinInput = (event) => {
        const minVal = Math.min(+event.target.value, maxValue - step);
        setMinValue(minVal);
        onChange === null || onChange === void 0 ? void 0 : onChange(minVal, maxValue, 0);
    };
    const handleMaxInput = (event) => {
        const maxVal = Math.max(+event.target.value, minValue + step);
        setMaxValue(maxVal);
        onChange === null || onChange === void 0 ? void 0 : onChange(minValue, maxVal, 1);
    };
    React.useEffect(() => {
        const minInput = minInputRef.current;
        const maxInput = maxInputRef.current;
        const handleMinChange = (event) => {
            var _a;
            const minVal = Math.min(+event.target.value, maxValueRef.current - step);
            setLeftThumbActivated(true);
            (_a = onAcceptValueRef.current) === null || _a === void 0 ? void 0 : _a.call(onAcceptValueRef, minVal, maxValueRef.current, 0);
        };
        const handleMaxChange = (event) => {
            var _a;
            setLeftThumbActivated(false);
            const maxVal = Math.max(+event.target.value, minValueRef.current + step);
            (_a = onAcceptValueRef.current) === null || _a === void 0 ? void 0 : _a.call(onAcceptValueRef, minValueRef.current, maxVal, 1);
        };
        minInput.addEventListener('change', handleMinChange);
        maxInput.addEventListener('change', handleMaxChange);
        return () => {
            minInput === null || minInput === void 0 ? void 0 : minInput.removeEventListener('change', handleMinChange);
            maxInput === null || maxInput === void 0 ? void 0 : maxInput.removeEventListener('change', handleMaxChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (React.createElement(Root, { ref: ref, className: classNames('multi-range-slider', className), styleState: styleState },
        React.createElement("input", { type: 'range', min: min, max: max, step: step, value: minValue, disabled: disabled, ref: minInputRef, "aria-label": ariaLabel, "aria-orientation": 'horizontal', "aria-valuenow": minValue, "aria-valuemin": min, "aria-valuemax": maxValue, "aria-valuetext": ariaValueTextMin, onInput: handleMinInput, className: classNames('range-input multi-range-slider--zindex-level-4', { 'multi-range-slider--zindex-level-5': leftThumbActivated }) }),
        React.createElement("input", { type: 'range', min: min, max: max, step: step, value: maxValue, disabled: disabled, ref: maxInputRef, "aria-orientation": 'horizontal', "aria-valuenow": maxValue, "aria-valuemin": minValue, "aria-valuemax": max, "aria-valuetext": ariaValueTextMax, onInput: handleMaxInput, className: classNames('range-input multi-range-slider--zindex-level-4', { 'multi-range-slider--zindex-level-5': !leftThumbActivated }) }),
        React.createElement(SliderRoot, { className: classNames('slider', { disabled }) },
            React.createElement("div", { className: 'multi-range-slider--track multi-range-slider--zindex-level-0' }),
            React.createElement("div", { ref: sliderRangeRef, className: 'multi-range-slider--range multi-range-slider--zindex-level-1' }),
            React.createElement("div", { ref: leftValueRef, className: classNames('multi-range-slider--value left-value multi-range-slider--zindex-level-2', { 'label-visible': tooltip, 'multi-range-slider--zindex-level-3': leftThumbActivated }) }, minValue),
            React.createElement("div", { ref: rightValueRef, className: classNames('multi-range-slider--value right-value multi-range-slider--zindex-level-2', { 'label-visible': tooltip, 'multi-range-slider--zindex-level-3': !leftThumbActivated }) }, maxValue))));
});
